import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import * as React from "react";

import {
  SkillSummary,
  UpdateSkillMetadataRequest,
} from "@/shared/domain/skills";
import {
  useKnownSkillTags,
  useUpdateSkillMetadata,
} from "@/shared/domain/skills/hooks";
import { Button } from "@/shared/foundation/primitives";

import styles from "./SkillEditor.module.css";
import { TagsAutocomplete } from "./TagsAutocomplete";

interface Props {
  open: boolean;
  onClose: () => void;
  /** The skill being edited. ``null`` means the dialog is closed. */
  summary: SkillSummary | null;
  orgId: string | undefined;
}

/**
 * Trim, drop empties, and dedupe case-insensitively. When `existing` is
 * supplied, a tag that matches an existing tag case-insensitively is
 * collapsed to the stored casing — without this, retyping a stored "QA"
 * as "qa" produces put_tags=["qa"] + remove_tags=["QA"], a silent
 * case-rename via the changeset.
 */
function normalizeTags(
  tags: string[],
  existing: readonly string[] = [],
): string[] {
  const existingByKey = new Map(existing.map((t) => [t.toLowerCase(), t]));
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of tags) {
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(existingByKey.get(key) ?? trimmed);
  }
  return result;
}

function diffTags(
  before: string[],
  after: string[],
): { put_tags: string[]; remove_tags: string[] } {
  const beforeSet = new Set(before);
  const afterSet = new Set(after);
  const put_tags = after.filter((t) => !beforeSet.has(t));
  const remove_tags = before.filter((t) => !afterSet.has(t));
  return { put_tags, remove_tags };
}

/**
 * Tag-only editor surfaced from the inline double-click affordance on the
 * Skills table. Distinct from :class:`SkillMetadataEditor` (which also edits
 * name and accessibility) so the user gets exactly the field they clicked
 * into — nothing else.
 */
export function TagsEditor({ open, onClose, summary, orgId }: Props) {
  const [tags, setTags] = React.useState<string[]>([]);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const knownTagsQuery = useKnownSkillTags(orgId);
  const updateMetadata = useUpdateSkillMetadata(orgId);

  // Reseed only when the dialog (re)opens or the target skill changes.
  // Key on skill_id, not on the `summary` reference: the parent re-derives
  // `summary` from the skills query each render, so depending on the object
  // identity would reseed (and clobber the user's in-progress edits) every
  // time `useUpdateSkillMetadata` invalidates the listing.
  React.useEffect(() => {
    if (!open || summary === null) {
      return;
    }
    setTags([...summary.skill.tags]);
    setErrorMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, summary?.skill.skill_id]);

  async function handleSave() {
    setErrorMessage(null);
    if (summary === null) {
      return;
    }
    // TagsAutocomplete commits pending text on blur — by the time this
    // handler runs, any typed-but-not-Enter'd tag is already in `tags`.
    const normalized = normalizeTags(tags, summary.skill.tags);
    const tagDiff = diffTags(summary.skill.tags, normalized);
    const tagsChanged =
      tagDiff.put_tags.length > 0 || tagDiff.remove_tags.length > 0;
    if (!tagsChanged) {
      onClose();
      return;
    }
    const request: UpdateSkillMetadataRequest = {};
    if (tagDiff.put_tags.length > 0) {
      request.put_tags = tagDiff.put_tags;
    }
    if (tagDiff.remove_tags.length > 0) {
      request.remove_tags = tagDiff.remove_tags;
    }
    try {
      await updateMetadata.mutateAsync({
        skillId: summary.skill.skill_id,
        request,
      });
      onClose();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to save.");
    }
  }

  const title =
    summary === null ? "Edit tags" : `Edit tags — ${summary.skill.name}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <div className={styles.formStack}>
          <TagsAutocomplete
            value={tags}
            onChange={setTags}
            options={knownTagsQuery.data ?? []}
            helperText="Free-form labels for grouping skills. Press Enter or click out to add."
          />
          {errorMessage && (
            <small className={styles.errorMessage}>{errorMessage}</small>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updateMetadata.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSave();
          }}
          disabled={updateMetadata.isPending}
          loading={updateMetadata.isPending}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
